import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { produce } from "immer";
import { SYSTEMS } from "@/utils/permissions";
import { filterObject } from "@/utils/helpers";

// Helper function to handle form errors
const handleFormErrors = (err, setFormError, t, defaultMessage) => {
  if (err?.response?.status === 422 && err?.response?.data?.errors) {
    const errors = err.response.data.errors;
    Object.keys(errors).forEach((field) => {
      setFormError(field, {
        type: "manual",
        message: errors[field][0],
      });
    });
  }
};

// ******* Queries *******

export const useGetInitialCampaignsQuery = (params = {}, currentSystem) => {
  const { axiosProtected } = useAxios();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  const url = `${system}/arrival-campaigns`;
  const query = filterObject(params);


  return useQuery({
    queryKey: ["initial-campaigns", system, query],
    queryFn: () =>
      axiosProtected.get(url, {
        params: query,
      }),
  });
};

export const useGetOneInitialCampaignQuery = ({ currentSystem }) => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  return useQuery({
    queryKey: ["one-initial-campaign", system, id],
    queryFn: () => axiosProtected.get(`${system}/arrival-campaigns/${id}`),
    enabled: !!id,
  });
};

// ******* Mutations *******

export const useCreateInitialCampaignMutation = ({currentSystem}) => {
  const { axiosProtected } = useAxios();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  return useMutation({
    mutationFn: ({ supervisor_mobile_number, phoneCountryCode, ...formData }) =>
      axiosProtected.post(`${system}/arrival-campaigns`, formData),
    onSuccess: (data) => {
      const id = data.data.data.id;
      toast.success(t("INITIAL_CAMPAIGN_CREATED_SUCCESSFULLY"));
      !!id && navigate(`/app/${currentSystem}/arrival-campaign/${id}/JED/view`);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || t("INITIAL_CAMPAIGN_CREATION_FAILED"),
      );
    },
  });
};

export const useUpdateInitialCampaignMutation = (setFormError, currentSystem) => {
  const { axiosProtected } = useAxios();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";

  return useMutation({
    mutationFn: async ({ ...formData }) => {
      return axiosProtected.post(
        `${system}/arrival-campaigns/update/${id}`,
        formData,
      );
    },
    onSuccess: (data, variables) => {
      toast.success(t("INITIAL_CAMPAIGN_UPDATED_SUCCESSFULLY"));

      const rowId = variables?.id ?? id;
      const cacheKey = ["one-initial-campaign", system, id];

      queryClient.setQueryData(cacheKey, (old) => {
        const formData = data?.data?.data;
        if (!formData) return old;
        if (!old?.data?.data) {
          return data;
        }
        const oldData = old.data.data;

        return produce(old, (draft) => {
          draft.data.data = {
            id: rowId,
            ...oldData,
            ...formData,
          };
        });
      });

      navigate(`/app/${currentSystem}/arrival-campaign/${rowId}/JED/view`);
    },
    onError: (err) => {
      console.error("Initial campaign update error:", err);

      if (setFormError) {
        handleFormErrors(
          err,
          setFormError,
          t,
          "INITIAL_CAMPAIGN_UPDATE_FAILED",
        );
      }
      toast.error(
        err?.response?.data?.message || t("INITIAL_CAMPAIGN_UPDATE_FAILED"),
      );
    },
  });
};

export const useDeleteInitialCampaignMutation = (currentSystem) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const system = currentSystem === SYSTEMS.MAKKAH.code ? "makkah" : "madina";
  return useMutation({
    mutationFn: ({ id }) => {
      return axiosProtected.delete(`${system}/arrival-campaigns/delete/${id}`);
    },
    onSuccess: () => {
      toast.success(t("INITIAL_CAMPAIGN_DELETED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["initial-campaigns"] });
    },
    onError: (err) => {
      console.error("Initial campaign delete error:", err);
      toast.error(
        err?.response?.data?.message || t("INITIAL_CAMPAIGN_DELETE_FAILED"),
      );
    },
  });
};
